import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { content } from "@/i18n/content";
import { EASE, Reveal, SectionHeading } from "@/components/Reveal";
import { avifSrc, isLocalPhoto, webpSrc } from "@/lib/media";

const Gallery = () => {
  const { pick } = useLanguage();
  const [active, setActive] = useState(null);
  const items = content.gallery.items;

  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (dir) => setActive((a) => (a === null ? null : (a + dir + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (active === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, step]);

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-[#F7F5F0] py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-24">
        <SectionHeading eyebrow={pick(content.gallery.eyebrow)} heading={pick(content.gallery.heading)} />

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
          {items.map((item, i) => (
            <Reveal key={item.src} delay={(i % 3) * 0.08} y={36} className="mb-5 break-inside-avoid">
              <button
                type="button"
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(i)}
                className="group relative block w-full overflow-hidden text-left"
                aria-label={pick(item.caption)}
              >
                <picture>
                  {isLocalPhoto(item.src) && (
                    <>
                      <source srcSet={avifSrc(item.src)} type="image/avif" />
                      <source srcSet={webpSrc(item.src)} type="image/webp" />
                    </>
                  )}
                  <img
                    src={item.src}
                    alt={pick(item.caption)}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.05]"
                  />
                </picture>
                <span className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                <span className="absolute bottom-0 left-0 p-5 text-sm font-medium text-[#F7F5F0] translate-y-3 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {pick(item.caption)}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={pick(items[active].caption)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0c0c0c]/95 backdrop-blur-md p-4 md:p-12"
            onClick={close}
          >
            <button
              type="button"
              data-testid="lightbox-close-btn"
              onClick={close}
              aria-label={pick(content.gallery.closeLabel)}
              className="absolute top-5 right-5 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] transition-colors hover:bg-[#C42021] hover:border-[#C42021]"
            >
              <X size={20} />
            </button>
            <button
              type="button"
              data-testid="lightbox-prev-btn"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous"
              className="absolute left-3 md:left-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] transition-colors hover:bg-[#F7F5F0] hover:text-[#171717]"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              data-testid="lightbox-next-btn"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next"
              className="absolute right-3 md:right-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] transition-colors hover:bg-[#F7F5F0] hover:text-[#171717]"
            >
              <ChevronRight size={20} />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <picture>
                {isLocalPhoto(items[active].src) && (
                  <>
                    <source srcSet={avifSrc(items[active].src)} type="image/avif" />
                    <source srcSet={webpSrc(items[active].src)} type="image/webp" />
                  </>
                )}
                <img
                  src={items[active].src}
                  alt={pick(items[active].caption)}
                  className="max-h-[78vh] w-auto max-w-full object-contain"
                />
              </picture>
              <figcaption className="mt-4 text-center text-sm tracking-wide text-[#F7F5F0]/80">
                {pick(items[active].caption)}
                <span className="ml-3 text-[#F7F5F0]/40">
                  {active + 1} / {items.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
